import React from 'react';
import { OverlayPanel } from '../ui/OverlayPanel';

interface QuestDefinition {
  id: string;
  name: string;
  description: string;
  objective: string;
  hint: string;
  lore?: string;
  questGiver?: {
    id: string;
    name: string;
    title: string;
    portrait: string;
  };
}

interface QuestJournalProps {
  isOpen: boolean;
  onClose: () => void;
  quest: QuestDefinition | null;
}

const FONT = {
  family: '"Sometype Mono", monospace',
  sizeSmall: 11,
  sizeMedium: 12,
  sizeLarge: 13,
  sizeXLarge: 14,
  weightNormal: 400,
  weightBold: 700,
};

const COLORS = {
  text: '#e6edf3',
  textSecondary: '#8b949e',
  textTertiary: '#94a3b8',
  bgPrimary: '#0d1117',
  bgSecondary: '#0f172a',
  bgTertiary: '#0b1220',
  border: '#30363d',
  borderLight: '#1f2937',
  accentBlue: '#58a6ff',
  accentGold: '#b8860b',
};

export const QuestJournal: React.FC<QuestJournalProps> = ({
  isOpen,
  onClose,
  quest,
}) => {
  if (!quest) return null;

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      title={`${quest.name.toUpperCase()} - JOURNAL`}
      draggable
      storageKey="questJournalPosition"
      defaultPosition={{ x: 300, y: 150 }}
      clampToViewport
      closeOnEscape
      closeOnBackdrop
      width={700}
    >
      <div
        style={{
          padding: '16px',
          fontFamily: FONT.family,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          maxHeight: '600px',
          overflowY: 'auto',
        }}
      >
        {/* Quest Giver Section */}
        {quest.questGiver && (
          <div
            style={{
              display: 'flex',
              gap: '12px',
              padding: '12px',
              backgroundColor: COLORS.bgTertiary,
              border: `1px solid ${COLORS.borderLight}`,
              borderRadius: '2px',
            }}
          >
            <img
              src={quest.questGiver.portrait}
              alt={quest.questGiver.name}
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '2px',
                border: `1px solid ${COLORS.border}`,
                objectFit: 'cover',
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%230b1220" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%238b949e" font-size="12"%3E[NPC]%3C/text%3E%3C/svg%3E';
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: FONT.sizeXLarge,
                  color: COLORS.accentBlue,
                  fontWeight: FONT.weightBold,
                  marginBottom: '4px',
                }}
              >
                {quest.questGiver.name}
              </div>
              <div
                style={{
                  fontSize: FONT.sizeMedium,
                  color: COLORS.textTertiary,
                }}
              >
                {quest.questGiver.title}
              </div>
            </div>
          </div>
        )}

        {/* Quest Description */}
        <div>
          <div
            style={{
              fontSize: FONT.sizeMedium,
              color: COLORS.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: '8px',
              fontWeight: FONT.weightBold,
            }}
          >
            QUEST DESCRIPTION
          </div>
          <div
            style={{
              fontSize: FONT.sizeMedium,
              color: COLORS.text,
              lineHeight: 1.6,
              padding: '8px',
              backgroundColor: COLORS.bgSecondary,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '2px',
            }}
          >
            {quest.description}
          </div>
        </div>

        {/* Objective */}
        <div>
          <div
            style={{
              fontSize: FONT.sizeMedium,
              color: COLORS.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: '8px',
              fontWeight: FONT.weightBold,
            }}
          >
            OBJECTIVE
          </div>
          <div
            style={{
              fontSize: FONT.sizeMedium,
              color: COLORS.text,
              lineHeight: 1.6,
              padding: '8px',
              backgroundColor: COLORS.bgSecondary,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '2px',
            }}
          >
            {quest.objective}
          </div>
        </div>

        {/* Lore Section */}
        {quest.lore && (
          <div>
            <div
              style={{
                fontSize: FONT.sizeMedium,
                color: COLORS.accentGold,
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: '8px',
                fontWeight: FONT.weightBold,
              }}
            >
              LORE & BACKGROUND
            </div>
            <div
              style={{
                fontSize: FONT.sizeMedium,
                color: COLORS.text,
                lineHeight: 1.8,
                padding: '12px',
                backgroundColor: COLORS.bgSecondary,
                border: `1px solid ${COLORS.borderLight}`,
                borderRadius: '2px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {quest.lore}
            </div>
          </div>
        )}

        {/* Hint */}
        {quest.hint && (
          <div
            style={{
              fontSize: FONT.sizeSmall,
              color: COLORS.textTertiary,
              fontStyle: 'italic',
              padding: '8px',
              backgroundColor: COLORS.bgTertiary,
              border: `1px solid ${COLORS.borderLight}`,
              borderRadius: '2px',
            }}
          >
            💡 {quest.hint}
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            backgroundColor: COLORS.accentBlue,
            color: COLORS.bgPrimary,
            border: 'none',
            borderRadius: '2px',
            fontFamily: FONT.family,
            fontSize: FONT.sizeMedium,
            fontWeight: FONT.weightBold,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.backgroundColor = '#79c0ff';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentBlue;
          }}
        >
          CLOSE
        </button>
      </div>
    </OverlayPanel>
  );
};
